import React, { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { Info, RotateCcw, Shuffle as ShuffleIcon, SlidersHorizontal } from 'lucide-react';
import { Badge } from '../Badge.jsx';
import { Button } from '../Button.jsx';
import { InputField, InputGroup } from '../InputGroup.jsx';
import { Select, SelectContent, SelectItem, SelectTrigger } from '../Select.jsx';
import { ToggleGroup, ToggleGroupItem } from '../ToggleGroup.jsx';
import { Tooltip, TooltipGroup } from '../Tooltip.jsx';

/**
 * The args form: one row per arg, the name and the control on one line. A
 * description stays hidden until its info button is pressed, then sits on
 * the line below. A boolean is a True/False toggle. A single
 * choice of up to three options is a toggle group; more than three is a
 * select. Shared by the shell's panel and the docs <Controls> block.
 *
 * argTypes: serialised (see preview/prepare.js serialiseArgTypes)
 * args: current values; values the browser cannot clone arrive as
 *       { __edlUnserialisable: 'function' } and render read-only.
 */
export function Controls({ argTypes = {}, args = {}, initialArgs = {}, onChange, onReset, compact = false, emptyText, actions = true }) {
  const rows = controllableRows(argTypes);
  const hidden = Object.entries(argTypes).filter(([, t]) => t.control === null && !t.disabled && args[t.name] !== undefined);

  if (!rows.length && !hidden.length) {
    return <p className="edl-controls__empty">{emptyText ?? 'This canvas has no args to change.'}</p>;
  }

  const size = compact ? 'compact' : 'default';
  const groups = groupBy(rows, ([, t]) => t.category ?? '');
  return (
    <div className={`edl-controls${compact ? ' edl-controls--compact' : ''}`}>
      {actions && (
        <ControlsActions argTypes={argTypes} args={args} initialArgs={initialArgs} onChange={onChange} onReset={onReset} />
      )}
      {[...groups].map(([category, items]) => (
        <div className="edl-controls__group" key={category || '_'}>
          {category && <p className="edl-controls__category">{category}</p>}
          {items.map(([key, type]) => (
            <ControlRow key={key} name={key} type={type} size={size} value={args[key]} initial={initialArgs[key]} onChange={(v) => onChange?.({ [key]: v })} />
          ))}
        </div>
      ))}
      {hidden.length > 0 && (
        <div className="edl-controls__group">
          <p className="edl-controls__category">Fixed in this story</p>
          {hidden.map(([key, type]) => (
            <div className="edl-field edl-field--static" key={key}>
              <span className="edl-field__label"><Badge>{type.name ?? key}</Badge></span>
              <div className="edl-field__control">
                <code className="edl-field__static">{preview(args[key])}</code>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// The count, shuffle and reset, above the fields.
export function ControlsActions({ argTypes = {}, args = {}, initialArgs = {}, onChange, onReset, className }) {
  const rows = controllableRows(argTypes);
  if (!onReset || !rows.length) return null;
  const changed = argsChanged(args, initialArgs);
  const shuffleable = rows.filter(([key, t]) => !(args[key] && typeof args[key] === 'object' && args[key].__edlUnserialisable) && randomValue(t, args[key]) !== undefined);
  return (
    <div className={['edl-controls__bar', className].filter(Boolean).join(' ')}>
      <span className="edl-controls__count">
        <SlidersHorizontal size={14} strokeWidth={1.5} aria-hidden="true" />
        {rows.length} {rows.length === 1 ? 'arg' : 'args'}
      </span>
      <TooltipGroup className="edl-controls__actions">
        {shuffleable.length > 0 && (
          <Tooltip content="Shuffle">
            <Button variant="ghost" size="compact" leadingIcon={ShuffleIcon} onClick={() => onChange?.(shuffle(shuffleable, args))} aria-label="Shuffle" />
          </Tooltip>
        )}
        <Tooltip content="Reset">
          <Button variant="ghost" size="compact" leadingIcon={RotateCcw} onClick={onReset} disabled={!changed} aria-label="Reset" />
        </Tooltip>
      </TooltipGroup>
    </div>
  );
}

export function argsChanged(args = {}, initialArgs = {}) {
  return Object.keys(args).some((key) => JSON.stringify(args[key]) !== JSON.stringify(initialArgs[key]));
}

function controllableRows(argTypes) {
  return Object.entries(argTypes).filter(([, t]) => t.control !== false && !t.disabled && t.control !== null);
}

// A random value for args with a finite choice: booleans, options, bounded
// numbers. Text, objects, colours and dates are left alone (undefined).
function randomValue(type, current) {
  const control = type.control ?? {};
  const pick = (list) => list[Math.floor(Math.random() * list.length)];
  switch (control.type) {
    case 'boolean':
      return Math.random() < 0.5;
    case 'select':
    case 'radio':
    case 'inline-radio':
      return type.options?.length > 1 ? pick(type.options) : undefined;
    case 'multi-select':
    case 'check':
    case 'inline-check':
      return type.options?.length ? type.options.filter(() => Math.random() < 0.5) : undefined;
    case 'range':
    case 'number': {
      const { min, max, step = 1 } = control;
      if (min == null || max == null) return undefined;
      return Math.min(max, min + Math.round((Math.random() * (max - min)) / step) * step);
    }
    default:
      return undefined;
  }
}

function shuffle(rows, args) {
  const patch = {};
  for (const [key, type] of rows) patch[key] = randomValue(type, args[key]);
  return patch;
}

function ControlRow({ name, type, size, value, initial, onChange }) {
  const id = useId();
  const [descriptionOpen, setDescriptionOpen] = useState(false);
  const [textPhase, setTextPhase] = useState('closed');
  const revealRef = useRef(null);
  const staggerRef = useRef(null);
  const hideTimer = useRef(0);
  useEffect(() => () => window.clearTimeout(hideTimer.current), []);
  // Accordion owns the height (data-open). Texts reveal owns the copy:
  // a blurred rise in, then a quiet fade out that does not replay the rise.
  const toggleDescription = () => {
    const block = staggerRef.current;
    window.clearTimeout(hideTimer.current);
    if (descriptionOpen) {
      revealRef.current?.setAttribute('data-open', 'false');
      block?.classList.add('is-hiding');
      block?.classList.remove('is-shown');
      setDescriptionOpen(false);
      setTextPhase('hiding');
      hideTimer.current = window.setTimeout(() => setTextPhase('closed'), 200);
      return;
    }
    revealRef.current?.setAttribute('data-open', 'true');
    if (block) {
      block.classList.remove('is-hiding');
      block.classList.remove('is-shown');
      void block.offsetHeight;
      block.classList.add('is-shown');
    }
    setDescriptionOpen(true);
    setTextPhase('shown');
  };
  const control = type.control ?? {};
  const label = type.name ?? name;
  const unserialisable = value && typeof value === 'object' && value.__edlUnserialisable;
  const modified = JSON.stringify(value) !== JSON.stringify(initial);
  const options = type.options ?? [];
  const labelledChoice = control.type === 'boolean' || isChoice(control.type);
  const labelId = labelledChoice ? `${id}-label` : undefined;
  const multiline = typeof value === 'string' && value.length > 60;
  const namedByField = !unserialisable && !multiline && (control.type == null || control.type === 'text');

  let field;
  if (unserialisable) {
    field = <code className="edl-field__static">{value.__edlUnserialisable}</code>;
  } else {
    switch (control.type) {
      case 'boolean':
        field = <BooleanField labelId={labelId} size={size} value={Boolean(value)} onChange={onChange} />;
        break;
      case 'number':
      case 'range':
        field = <NumberField id={id} value={value} control={control} onChange={onChange} range={control.type === 'range'} />;
        break;
      case 'color':
        field = <ColorField id={id} value={value} onChange={onChange} />;
        break;
      case 'select':
      case 'radio':
      case 'inline-radio':
        field = <ChoiceField id={id} labelId={labelId} size={size} value={value} options={options} labels={type.labels} onChange={onChange} />;
        break;
      case 'multi-select':
      case 'check':
      case 'inline-check':
        field = <CheckField id={id} value={Array.isArray(value) ? value : []} options={options} labels={type.labels} onChange={onChange} inline={control.type !== 'check'} />;
        break;
      case 'object':
        field = <ObjectField id={id} value={value} onChange={onChange} />;
        break;
      case 'date':
        field = <DateField id={id} value={value} onChange={onChange} />;
        break;
      case 'file':
        field = <span className="edl-field__static">File inputs are not supported yet.</span>;
        break;
      case 'text':
      default:
        field = <TextField id={id} label={type.required ? `${label}, required` : label} size={size} value={value} onChange={onChange} multiline={multiline} />;
    }
  }

  return (
    <div className={`edl-field edl-field--${control.type ?? 'text'}${modified ? ' is-modified' : ''}`} data-arg={name}>
      <div className="edl-field__name">
        <label className="edl-field__label" id={labelId} htmlFor={labelledChoice ? undefined : id} aria-hidden={namedByField ? true : undefined}>
          <Badge>
            <span className={`edl-badge__lead${modified ? ' is-on' : ''}`} aria-hidden="true">
              <span className="edl-badge__lead-clip">
                <span className="edl-badge__mark" />
              </span>
            </span>
            {label}
            {type.required && <span className="edl-field__required" aria-label="required">*</span>}
          </Badge>
        </label>
        {type.description && (
          <Button
            className="edl-field__info"
            variant="ghost"
            size="compact"
            leadingIcon={Info}
            aria-expanded={descriptionOpen}
            aria-controls={`${id}-desc`}
            aria-label={`${descriptionOpen ? 'Hide' : 'Show'} description for ${label}`}
            tooltip={`${descriptionOpen ? 'Hide' : 'Show'} description`}
            onClick={toggleDescription}
          />
        )}
      </div>
      <div className="edl-field__control">{field}</div>
      {type.description && (
        <div ref={revealRef} className="edl-field__reveal t-acc" data-open={descriptionOpen ? 'true' : 'false'}>
          <div className="t-acc-panel">
            <div className="t-acc-panel-inner">
              <div
                ref={staggerRef}
                className={`t-stagger${textPhase === 'shown' ? ' is-shown' : ''}${textPhase === 'hiding' ? ' is-hiding' : ''}`}
              >
                <p id={`${id}-desc`} className="edl-field__description t-stagger-line t-stagger-line--1">
                  {inlineMarkdown(type.description)}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function TextField({ id, label, size, value, onChange, multiline }) {
  const [draft, setDraft] = useState(value ?? '');
  useEffect(() => setDraft(value ?? ''), [value]);
  const commit = (next) => {
    setDraft(next);
    onChange(next);
  };
  // A long string stays a textarea. InputField is a single 36px line.
  if (multiline) {
    return <textarea id={id} className="edl-input edl-input--area" rows={3} value={draft} onChange={(e) => commit(e.target.value)} />;
  }
  return (
    <InputGroup size={size}>
      <InputField index={0} id={id} label={label} labelHidden value={draft} onChange={commit} />
    </InputGroup>
  );
}

function NumberField({ id, value, control, onChange, range }) {
  const parse = (v) => (v === '' ? undefined : Number(v));
  return (
    <div className="edl-number">
      {range && (
        <input
          className="edl-range"
          type="range"
          min={control.min ?? 0}
          max={control.max ?? 100}
          step={control.step ?? 1}
          value={value ?? control.min ?? 0}
          onChange={(e) => onChange(parse(e.target.value))}
          aria-hidden="true"
          tabIndex={-1}
        />
      )}
      <input id={id} className="edl-input edl-input--number" type="number" min={control.min} max={control.max} step={control.step} value={value ?? ''} onChange={(e) => onChange(parse(e.target.value))} />
    </div>
  );
}

function isChoice(type) {
  return type === 'select' || type === 'radio' || type === 'inline-radio';
}

function BooleanField({ labelId, size, value, onChange }) {
  return (
    <ToggleGroup
      size={size}
      aria-labelledby={labelId}
      value={[value ? 'true' : 'false']}
      onValueChange={(next) => {
        if (next[0] === 'true') onChange(true);
        else if (next[0] === 'false') onChange(false);
      }}
    >
      <ToggleGroupItem value="true">True</ToggleGroupItem>
      <ToggleGroupItem value="false">False</ToggleGroupItem>
    </ToggleGroup>
  );
}

function optionKey(option) {
  return typeof option === 'string' || typeof option === 'number' ? String(option) : JSON.stringify(option);
}

function optionLabel(option, labels) {
  const raw = typeof option === 'string' || typeof option === 'number' ? option : JSON.stringify(option);
  return labels?.[raw] ?? String(raw);
}

function ChoiceField({ id, labelId, size, value, options, labels, onChange }) {
  const fitRef = useRef(null);
  const [availableWidth, setAvailableWidth] = useState(null);
  const [toggleWidth, setToggleWidth] = useState(null);
  const current = options.find((option) => JSON.stringify(option) === JSON.stringify(value));
  const currentKey = current === undefined ? null : optionKey(current);
  const geometryKey = `${size}:${options.map((option) => optionLabel(option, labels)).join('|')}`;
  const condensed = options.length > 3 || (availableWidth != null && toggleWidth != null && toggleWidth > availableWidth);
  const pick = (key) => {
    const option = options.find((item) => optionKey(item) === key);
    if (option !== undefined) onChange(option);
  };

  useLayoutEffect(() => {
    const control = fitRef.current?.parentElement;
    if (!control) return undefined;
    const measure = () => setAvailableWidth(control.clientWidth);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(control);
    return () => observer.disconnect();
  }, []);

  useLayoutEffect(() => {
    if (options.length > 3 || condensed) return;
    const toggle = fitRef.current?.querySelector('.edl-toggle-group');
    if (toggle) setToggleWidth(Math.ceil(toggle.getBoundingClientRect().width));
  }, [condensed, geometryKey, options.length]);

  return (
    <span ref={fitRef} className="edl-field__choice">
      {condensed ? (
      <Select size={size} value={currentKey} onValueChange={pick}>
        <SelectTrigger id={id} aria-labelledby={labelId} placeholder="Choose…" />
        <SelectContent>
          {options.map((option) => (
            <SelectItem key={optionKey(option)} value={optionKey(option)}>
              {optionLabel(option, labels)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      ) : (
        <ToggleGroup
          size={size}
          aria-labelledby={labelId}
          value={currentKey == null ? [] : [currentKey]}
          onValueChange={(next) => { if (next[0] != null) pick(next[0]); }}
        >
          {options.map((option) => (
            <ToggleGroupItem key={optionKey(option)} value={optionKey(option)}>
              {optionLabel(option, labels)}
            </ToggleGroupItem>
          ))}
        </ToggleGroup>
      )}
    </span>
  );
}

function CheckField({ id, value, options, labels, onChange, inline }) {
  const has = (o) => value.some((v) => JSON.stringify(v) === JSON.stringify(o));
  return (
    <div className={`edl-choices${inline ? ' edl-choices--inline' : ''}`}>
      {options.map((o, i) => (
        <label className="edl-check" key={i}>
          <input
            type="checkbox"
            className="edl-check__input"
            id={i === 0 ? id : undefined}
            checked={has(o)}
            onChange={(e) => onChange(e.target.checked ? [...value, o] : value.filter((v) => JSON.stringify(v) !== JSON.stringify(o)))}
          />
          <span className="edl-check__box" aria-hidden="true" />
          <span className="edl-check__text">{optionLabel(o, labels)}</span>
        </label>
      ))}
    </div>
  );
}

function ColorField({ id, value, onChange }) {
  const hex = typeof value === 'string' && /^#[0-9a-f]{6}$/i.test(value) ? value : '#000000';
  return (
    <div className="edl-color">
      <input className="edl-color__swatch" type="color" value={hex} onChange={(e) => onChange(e.target.value)} aria-label="Pick a colour" />
      <input id={id} className="edl-input" type="text" value={value ?? ''} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}

function DateField({ id, value, onChange }) {
  const d = value ? new Date(value) : null;
  const str = d && !Number.isNaN(d.valueOf()) ? d.toISOString().slice(0, 10) : '';
  return <input id={id} className="edl-input" type="date" value={str} onChange={(e) => onChange(e.target.value ? new Date(e.target.value).valueOf() : undefined)} />;
}

function ObjectField({ id, value, onChange }) {
  const [draft, setDraft] = useState(() => JSON.stringify(value ?? null, null, 2));
  const [error, setError] = useState(null);
  useEffect(() => {
    setDraft(JSON.stringify(value ?? null, null, 2));
    setError(null);
  }, [JSON.stringify(value)]);
  return (
    <>
      <textarea
        id={id}
        className={`edl-input edl-input--area edl-input--code${error ? ' is-invalid' : ''}`}
        rows={Math.min(10, draft.split('\n').length + 1)}
        spellCheck={false}
        value={draft}
        onChange={(e) => {
          setDraft(e.target.value);
          try {
            const parsed = JSON.parse(e.target.value);
            setError(null);
            onChange(parsed);
          } catch (err) {
            setError('Not valid JSON yet');
          }
        }}
      />
      {error && <p className="edl-field__error">{error}</p>}
    </>
  );
}

function preview(v) {
  if (v && typeof v === 'object' && v.__edlUnserialisable) return v.__edlUnserialisable;
  try {
    const s = JSON.stringify(v);
    return s.length > 80 ? s.slice(0, 77) + '…' : s;
  } catch {
    return String(v);
  }
}

function groupBy(items, fn) {
  const map = new Map();
  for (const item of items) {
    const k = fn(item);
    if (!map.has(k)) map.set(k, []);
    map.get(k).push(item);
  }
  return map;
}

/** `code`, **bold**, *em* and [links](url) in one-line descriptions. */
export function inlineMarkdown(text) {
  const parts = [];
  const re = /(`[^`]+`)|(\*\*[^*]+\*\*)|(\*[^*]+\*)|(\[[^\]]+\]\([^)]+\))/g;
  let last = 0;
  let m;
  let i = 0;
  while ((m = re.exec(text))) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    const tok = m[0];
    if (tok.startsWith('`')) parts.push(<code key={i++}>{tok.slice(1, -1)}</code>);
    else if (tok.startsWith('**')) parts.push(<strong key={i++}>{tok.slice(2, -2)}</strong>);
    else if (tok.startsWith('*')) parts.push(<em key={i++}>{tok.slice(1, -1)}</em>);
    else {
      const [, label, href] = tok.match(/\[([^\]]+)\]\(([^)]+)\)/);
      parts.push(<a key={i++} href={href} target="_blank" rel="noreferrer">{label}</a>);
    }
    last = m.index + tok.length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
}
